/**
 * <aa-fbt-fetch> - swaps the Frequently Bought Together widget's fallback rows for
 * Shopify's algorithmic recommendations for the product being viewed.
 *
 * Why this is a private element rather than Dawn's <product-recommendations>:
 *
 *   global.js's ProductRecommendations locates its payload with
 *
 *     const i = t.querySelector("product-recommendations")
 *
 *   which takes the FIRST such element in the response. sections/main-product.liquid
 *   already contains one, inside the complementary-products block. That block is not in
 *   templates/product.json today, so the collision is dormant - but were a merchant to
 *   enable it from the editor, both elements would match and whichever came first in the
 *   response would be swapped in, silently, into the wrong place. A private tag cannot
 *   collide with it, or with any complementary block added later.
 *
 * The markup contract, both halves of which live in snippets/v2-fbt-widget.liquid:
 *
 *   - The live element carries data-url and wraps ONLY the widget's <form>.
 *   - The fetched response contains <aa-fbt-fetch> wrapping a form-only render of the
 *     same widget - no wrapper card, no heading, no <style>, no <script>.
 *
 * so the swap replaces rows and nothing else. The card, its heading and its scoped styles
 * are outside the swapped region and survive untouched.
 *
 * When the fetch comes back with no recommendations, the response's <aa-fbt-fetch> is
 * empty and NOTHING is replaced. That is deliberate: the merchant's fallback list, already
 * server-rendered and working, simply stays. It is also what makes intent=complementary
 * safe to select today - complementaries return an empty product list for this store until
 * they are curated in Search & Discovery, and an empty response is a no-op rather than an
 * empty widget.
 *
 * The swapped-in form is inert as far as the browser is concerned - scripts inserted
 * through innerHTML never execute - so the widget's own script re-binds it with a
 * MutationObserver. Nothing here needs to know about that.
 */
class AaFbtFetch extends HTMLElement {
  connectedCallback() {
    // No URL, no work. connectedCallback runs for EVERY element the parser or a swap
    // inserts into the document - including the shells that arrive inside a fetched
    // payload. Those are pure structure (they carry the reserved blank space) and must
    // stay inert; if they fetched, each swap would spawn another fetch, which would
    // deliver another shell with a URL, forever.
    //
    // This guard is the real safety net. The server also withholds data-url from the
    // payload, but a fetch here must depend on the URL existing rather than on the
    // server-side flag staying in sync with this file.
    if (!this.dataset.url) return;

    // The cart drawer is a special case, and it is the reason this is not just an observer.
    //
    // A closed drawer is parked OFF-CANVAS to the right - measured left=1435 on a 1400px
    // viewport - and the rootMargin below only extends downwards. With no horizontal
    // margin the element sits outside the observation box, so isIntersecting is never true
    // and the callback never runs: the widget would sit on its spinner forever. Widening
    // the margin far enough to reach an off-canvas element would also make every other
    // placement fetch far earlier than it needs to.
    //
    // There is also nothing to defer here. The drawer only exists when the cart has items,
    // and the visitor opens it within a second or two of adding one - deferring the fetch
    // would only mean watching the spinner after opening. So fetch immediately.
    if (this.closest('cart-drawer')) {
      this.load();

      // Watchdog. The drawer is re-rendered from cart-drawer.liquid on every cart change,
      // so this element is created fresh each time and starts on the server-rendered
      // waiting state. If that request never settles - slow connection, a stalled socket,
      // a request the browser holds open - the spinner would sit there for the life of the
      // page, which is the worst possible outcome for a widget that is visible every time
      // the drawer opens.
      //
      // load() always clears --loading when it resolves, successfully or not, so this can
      // only fire when nothing came back at all. It removes the class rather than disposing
      // the widget, so the server-rendered form underneath becomes usable instead of the
      // whole bundle vanishing.
      setTimeout(() => {
        if (this.isConnected && this.classList.contains('v2-fbt--loading')) {
          this.classList.remove('v2-fbt--loading');
        }
      }, 8000);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  // The fetch and the swap. Pulled out of connectedCallback so the drawer path can call it
  // directly and the observer path can call it on intersection - same work either way.
  load() {
    if (this.dataset.loaded === '1') return;
    this.dataset.loaded = '1';

    // Nothing is added here. --loading arrives already set from the server render, so
    // the waiting state is what the browser paints first and there is no window in
    // which the fallback products can be seen. Adding it from the observer callback
    // instead was the cause of a visible flash: observer callbacks are asynchronous, so
    // the fallback rows were laid out and painted before the class ever landed.
    //
    // The height of the blank space is likewise stated in CSS, not measured here.
    //
    // This function is now only ever a removal, and only on the two paths below where
    // there is nothing to show. Leaving the class on would keep the card blank forever
    // over a form that is still perfectly usable underneath.
    const done = () => this.classList.remove('v2-fbt--loading');

    // 400px, matching Dawn's ProductRecommendations: the swap is normally done well
    // before the widget is on screen, so the customer never sees the rows change.
    fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const parsed = document.createElement('div');
            parsed.innerHTML = text;

            const fetched = parsed.querySelector('aa-fbt-fetch');
            // Count ROWS, not content. The payload always contains a form, so a length
            // check would pass on a response with zero products and swap in an empty
            // widget - the guard has to test the thing that is actually absent.
            //
            // No rows means Shopify has no recommendations for this product: leave the
            // element alone and let done() below take the blank space down. There is a
            // server-rendered fallback underneath it, and it simply re-emerges.
            const rows = fetched ? fetched.querySelectorAll('.v2-fbt__row').length : 0;
            if (!fetched || rows < 2) return;

            this.innerHTML = fetched.innerHTML;
            // The swapped-in markup carries no --loading, so the fresh form is already
            // visible. done() still runs below, off the same chain, and finds nothing to
            // undo - harmless, and it keeps both outcomes on one code path.
          })
          .catch((error) => {
            // Never surface this: the fallback rows are still there and still work.
            console.error(error);
          })
          .then(done);
  }

  disconnectedCallback() {
    // The swap replaces this element's contents, which detaches the form and the blank
    // space but leaves the element itself in place - so this normally does not run. It is
    // here for the case where the widget is removed outright (the cart drawer re-rendering
    // its contents, an editor block being deleted): without it the observer would stay
    // registered on a detached node for the life of the page.
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
  }
}

customElements.define('aa-fbt-fetch', AaFbtFetch);
/* ---------------------------------------------------------------------------
 * Widget behaviour: running total, checkbox gating, and the add-all submit.
 *
 * This IIFE used to be emitted inline from snippets/v2-fbt-widget.liquid. It
 * lives here because it has to run on EVERY page, not just pages that already
 * have a widget on them.
 *
 * The cart drawer can acquire an FBT widget at any time - a quick-add from a
 * collection page re-renders .drawer__inner from cart-drawer.liquid, and that
 * render includes the widget. Markup inserted via innerHTML never executes the
 * <script> it carries, so the widget arrives inert. The MutationObserver below
 * is the ONLY thing that re-binds it.
 *
 * Which means the guard that used to open this IIFE -
 *
 *     var roots = document.querySelectorAll("[data-fbt]");
 *     if (!roots.length) return;
 *
 * was self-defeating: on a page with no widget yet it returned before reaching
 * the observer, so the one case that needed the observer was the one case that
 * never installed it. The initial pass now simply does nothing when there are no
 * roots, and the observer is always installed.
 *
 * Symptom this fixes: add to cart from a collection page with an empty cart, and
 * the drawer opens with a greyed-out, unclickable "Add all to cart". The button
 * is server-rendered `hidden disabled`, and `hidden` does not hide it - .button
 * sets display:inline-flex at author level, which outranks the UA
 * [hidden]{display:none} - so only the disabled state was visible.
 * --------------------------------------------------------------------------- */
(function () {
  // data-fbt-price is a plain decimal string ('36.00'), so rounding it to cents
  // avoids float drift when several are summed.
  function toCents(raw) {
    var n = parseFloat(raw);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  // Mirrors snippets/price.liquid: the store's format string, not a hardcoded $.
  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withDelimiters = parts.join('.');
    var noDecimals = parts[0];
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimals.replace(/,/g, '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withDelimiters.replace(/,/g, ' ').replace('.', ',').replace(/ /g, '.'))
      .replace(/\{\{\s*amount\s*\}\}/, withDelimiters);
  }

  // Setup for one [data-fbt] root. Named rather than inlined because the observer
  // below has to run it again on content that arrives after this script did.
  function initRoot(root) {
    var form = root.querySelector('[data-fbt-form]');
    var totalEl = root.querySelector('[data-fbt-total]');
    var submitBtn = root.querySelector('[data-fbt-submit]');
    var format = root.dataset.fbtMoneyFormat;
    if (!form || !totalEl) return;

    // This script is emitted once per widget instance (cart drawer, product block,
    // full-width section) and each copy walks EVERY [data-fbt] root. Without this
    // guard each copy binds another submit listener to the same form, so one click
    // fires the add N times - measured a 3x bundle add on a page with three
    // instances. The data attribute is the idempotency marker; it lives on the FORM,
    // so a form the recommendations fetch has replaced is a new, unbound one and is
    // set up from scratch.
    if (form.dataset.fbtBound === '1') {
      // still re-run render() so a re-rendered drawer reflects current state
      if (typeof form.__fbtRender === 'function') form.__fbtRender();
      return;
    }
    form.dataset.fbtBound = '1';

    function render() {
      var checks = form.querySelectorAll('[data-fbt-check]');
      var allInputs = form.querySelectorAll('[data-fbt-item-input]');
      var total = 0;
      var anyChecked = false;

      allInputs.forEach(function (input) { input.disabled = true; });

      checks.forEach(function (check) {
        var row = check.closest('.v2-fbt__row');
        var inputs = row ? row.querySelectorAll('[data-fbt-item-input]') : [];
        if (check.checked) {
          anyChecked = true;
          inputs.forEach(function (input) { input.disabled = false; });
          total += toCents(check.dataset.fbtPrice);
        }
      });

      totalEl.textContent = money(total, format);
      if (submitBtn) {
        // revealed here rather than at parse time, so the button never appears
        // before the total has been reconciled
        submitBtn.hidden = false;
        submitBtn.disabled = !anyChecked;
      }
      if (anyChecked) {
        var notice = root.querySelector('.v2-fbt__error');
        if (notice) notice.remove();
      }
    }

    form.addEventListener('change', function (event) {
      if (event.target.matches('[data-fbt-check]')) render();
    });

    // Native POST is the baseline. When fetch succeeds we hand the response to the
    // cart drawer instead of letting the browser navigate and reload the page.
    form.addEventListener('submit', function (event) {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return;

      event.preventDefault();
      if (submitBtn) submitBtn.disabled = true;

      var body = new FormData(form);
      if (typeof drawer.getSectionsToRender === 'function') {
        body.append(
          'sections',
          drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',')
        );
      }
      body.append('sections_url', window.location.pathname);

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then(function (res) { return res.json(); })
        .then(function (state) {
          if (state.status) throw new Error(state.description || 'Could not add to cart');
          drawer.renderContents(state);
        })
        .catch(function (error) {
          // Put the selection back and show the reason rather than failing silently.
          var notice = form.querySelector('.v2-fbt__error');
          if (!notice) {
            notice = document.createElement('p');
            notice.className = 'v2-fbt__error';
            notice.style.cssText = 'color:#800000;font-size:13px;margin:0 0 8px;';
            form.querySelector('.v2-fbt__footer').prepend(notice);
          }
          notice.textContent = error && error.message ? error.message : 'Could not add to cart.';
          if (submitBtn) submitBtn.disabled = false;
        });
    });

    form.__fbtRender = render;
    render();
  }

  document.querySelectorAll('[data-fbt]').forEach(initRoot);

  // Anything that replaces this widget's markup AFTER this script has run leaves it
  // inert, because a <script> inserted through innerHTML never executes. Two things do
  // that, and both need the same treatment:
  //
  //   1. The recommendations fetch above. assets/aa-fbt-widget.js copies the fetched
  //      form into <aa-fbt-fetch>, so the new form has no submit/change listeners and
  //      its Add-all button is still server-rendered `hidden disabled` - the customer
  //      sees rows they can tick and a button that does nothing.
  //   2. The cart drawer, whose .drawer__inner is replaced from cart-drawer.liquid on
  //      every cart change.
  //
  // One observer, flagged on window because this script is emitted once per widget
  // instance. Without the flag three copies would each watch document.body, so every
  // DOM change anywhere would run the walk below three times over. initRoot is
  // idempotent either way - this is about not doing the work three times, not about
  // correctness.
  if (!window.__fbtWatch) {
    window.__fbtWatch = true;
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        Array.prototype.forEach.call(record.addedNodes, function (node) {
          // Text/comment nodes have no matches() or querySelectorAll().
          if (node.nodeType !== 1) return;
          // Case 1: the fetch or drawer adds a whole new root.
          if (node.matches && node.matches('[data-fbt]')) initRoot(node);
          // Case 2: it adds something INSIDE a root, or adds a root that contains
          // others further down.
          if (node.querySelectorAll) {
            Array.prototype.forEach.call(node.querySelectorAll('[data-fbt]'), initRoot);
          }
          // Case 3, and the one that actually fires for the recommendations fetch: the
          // swapped-in node IS the form, so the root it belongs to is an ancestor the
          // added subtree does not contain. closest() reaches it; nothing above does.
          var owner = node.closest && node.closest('[data-fbt]');
          if (owner) initRoot(owner);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
